import React, { useRef, useState } from 'react';
import { Upload, X, FileText, Video, Package, File } from 'lucide-react';
import './UploadArea.css';

interface UploadAreaProps {
  onUpload: (file: File) => void | Promise<void>;
  onRemove?: () => void;
  file?: File | null;
  icon?: React.ElementType;
  title?: string;
  description?: string;
  accept?: string;
  maxSize?: number; // in MB
  disabled?: boolean;
}

const UploadArea: React.FC<UploadAreaProps> = ({
  onUpload,
  onRemove,
  file,
  icon: Icon = Upload,
  title = 'Upload File',
  description = 'Click to browse or drag and drop',
  accept,
  maxSize = 500, // 500MB default
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = async (selectedFile: File) => {
    // Check file size
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      alert(`File size exceeds ${maxSize}MB limit`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for user feedback
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      await onUpload(selectedFile);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Reset after a short delay
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleClick = () => {
    if (!disabled && !file) {
      fileInputRef.current?.click();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled && !file) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (disabled || file) return;

    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onRemove) {
      onRemove();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'mp4' || ext === 'mov' || ext === 'avi') return Video;
    if (ext === 'zip') return Package;
    if (ext === 'pdf' || ext === 'doc' || ext === 'docx') return FileText;
    return File;
  };

  return (
    <div
      className={`upload-area ${isDragging ? 'upload-area-dragging' : ''} ${
        file ? 'upload-area-has-file' : ''
      } ${disabled ? 'upload-area-disabled' : ''}`}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="upload-area-input"
        onChange={handleFileChange}
        accept={accept}
        disabled={disabled}
        style={{ display: 'none' }}
      />

      {isUploading ? (
        <div className="upload-area-uploading">
          <div className="upload-area-progress-circle">
            <svg width="60" height="60">
              <circle
                cx="30"
                cy="30"
                r="25"
                stroke="#e0e0e0"
                strokeWidth="4"
                fill="none"
              />
              <circle
                cx="30"
                cy="30"
                r="25"
                stroke="#FF8C00"
                strokeWidth="4"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 25}`}
                strokeDashoffset={`${2 * Math.PI * 25 * (1 - uploadProgress / 100)}`}
                transform="rotate(-90 30 30)"
                style={{ transition: 'stroke-dashoffset 0.3s' }}
              />
            </svg>
            <span className="upload-area-progress-text">{uploadProgress}%</span>
          </div>
          <p className="upload-area-uploading-text">Uploading...</p>
        </div>
      ) : file ? (
        <div className="upload-area-file-info">
          {(() => {
            const FileIcon = getFileIcon(file.name);
            return <FileIcon size={40} className="upload-area-file-icon" />;
          })()}
          <div className="upload-area-file-details">
            <p className="upload-area-file-name">{file.name}</p>
            <p className="upload-area-file-size">{formatFileSize(file.size)}</p>
          </div>
          {onRemove && (
            <button
              className="upload-area-remove-btn"
              onClick={handleRemove}
              type="button"
            >
              <X size={20} />
            </button>
          )}
        </div>
      ) : (
        <div className="upload-area-empty">
          <Icon size={48} className="upload-area-icon" />
          <p className="upload-area-title">{title}</p>
          <p className="upload-area-description">{description}</p>
        </div>
      )}
    </div>
  );
};

export default UploadArea;
